import React, { useCallback, useMemo, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { getPersonFullName } from '@/components/library-v2/people/lib'
import { PersonAvatar } from '@/components/library-v2/people/Table/cells'
import { getComboboxTriggerClassName } from '@/components/library-v2/ui/Combobox/lib'

import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

interface PersonComboboxProps {
  value: UUID | null
  onChange: (personId: UUID | null) => void
  people?: EntityPerson[]
  placeholder?: string
  disabled?: boolean
}

const VISIBLE_PERSON_LIMIT = 50

const getPersonSearchText = (person: EntityPerson) =>
  [getPersonFullName(person), person.email, person.alternate_email, person.role]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

export const PersonCombobox: React.FC<PersonComboboxProps> = ({
  value,
  onChange,
  people,
  placeholder = 'Select person...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQuery('')
  }, [])

  const handleSelect = useCallback(
    (personId: UUID) => () => {
      onChange(personId)
      setOpen(false)
      setQuery('')
    },
    [onChange, setOpen],
  )

  const handleClear = useCallback(() => {
    onChange(null)
    setOpen(false)
    setQuery('')
  }, [onChange])

  const selectedPerson = people?.find((p) => p.id === value)
  const visiblePeople = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const allPeople = people ?? []
    const filteredPeople = normalizedQuery
      ? allPeople.filter((person) =>
          getPersonSearchText(person).includes(normalizedQuery),
        )
      : allPeople
    return filteredPeople.slice(0, VISIBLE_PERSON_LIMIT)
  }, [people, query])

  return (
    <Combobox open={open} onOpenChange={handleOpenChange}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={getComboboxTriggerClassName({ disabled })}
        >
          {selectedPerson ? (
            <PersonItem person={selectedPerson} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent
        align="start"
        className="p-0 w-[360px]"
        shouldFilter={false}
      >
        <ComboboxInput
          placeholder="Search people..."
          className="placeholder:text-sm"
          value={query}
          onValueChange={setQuery}
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">No people found.</ComboboxEmpty>
          <ComboboxGroup>
            {value ? (
              <ComboboxSelectableItem
                key="__clear__"
                value="__clear__"
                isSelected={false}
                onSelect={handleClear}
              >
                <span className="text-sm text-red-600 dark:text-red-400">
                  Clear selection
                </span>
              </ComboboxSelectableItem>
            ) : null}
            {visiblePeople.map((person) => {
              const name = getPersonFullName(person)
              return (
                <ComboboxSelectableItem
                  key={person.id}
                  value={`${name}-${person.id}`}
                  isSelected={value === person.id}
                  onSelect={handleSelect(person.id)}
                >
                  <div className="flex items-center gap-2 truncate">
                    <PersonAvatar person={person} width={18} height={18} />
                    {name.length > 0 ? (
                      <span className="text-sm truncate">{name}</span>
                    ) : (
                      <span className="text-sm truncate text-primary-500 dark:text-primary-400">
                        Name not available
                      </span>
                    )}
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface PersonItemProps {
  person: EntityPerson
}

const PersonItem: React.FC<PersonItemProps> = ({ person }) => {
  return (
    <div className="flex items-center gap-2 truncate">
      <PersonAvatar person={person} width={18} height={18} />
      <span className="text-sm truncate">{getPersonFullName(person)}</span>
    </div>
  )
}

export const PersonSelect: React.FC<PersonComboboxProps> = ({
  value,
  onChange,
  people,
  placeholder = 'Select person...',
  disabled = false,
}) => {
  const handleValueChange = useCallback(
    (personId: string) => {
      onChange(personId as UUID)
    },
    [onChange],
  )

  const selectedPerson = people?.find((p) => p.id === value)

  return (
    <Select
      value={value || ''}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full dark:bg-default-dark">
        <SelectValue placeholder={placeholder}>
          {selectedPerson ? (
            <PersonItem person={selectedPerson} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {people && people.length > 0 ? (
          people.map((person) => (
            <SelectItem key={person.id} value={person.id}>
              <PersonItem person={person} />
            </SelectItem>
          ))
        ) : (
          <div className="text-sm truncate px-2 py-[5px] text-primary-500 dark:text-primary-400">
            <span className="text-sm truncate">No people found</span>
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
